/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */

import { useQuery } from '@tanstack/react-query';

import { qualifyUrl } from 'util/URLUtils';

import fetch from 'logic/rest/FetchProvider';

import { defaultOnError } from 'util/conditional/onError';

import PaginationURL from 'util/PaginationURL';
import type { SearchParams } from 'stores/PaginationTypes';
import FiltersForQueryParams from 'components/common/EntityFilters/FiltersForQueryParams';
import type { PaginatedResponse } from 'components/common/PaginatedEntityTable/useFetchEntities';

export type PaginationInfo = {
  per_page: number;
  total: number;
  count: number;
  page: number;
}
export type FilterOption = {
  title: string;
  value: string;
}
export type PaginationParameters = {
  per_page: number;
  query: string;
  page: number;
  sort_by: string;
  order: string;
}
export type RestoreIndex = {
  index_set_id: string;
  index_name: string;
}
export type Sorting = {
  id: string;
  direction: 'ASC' | 'DESC';
}
export type EntityDefaults = {
  sort: Sorting;
}
export type EntityAttribute = {
  related_property: string;
  filterable: boolean;
  related_collection: string;
  hidden: boolean;
  id: string;
  sortable: boolean;
  type: 'STRING' | 'DATE' | 'DOUBLE' | 'INT' | 'LONG' | 'OBJECT_ID' | 'BOOLEAN';
  title: string;
  searchable: boolean;
  filter_options: FilterOption[];
}
export type DataLakeRetrievalResult = {
  timestamp_to: string;
  restore_indices: RestoreIndex[];
  retrieved_documents: number;
  index_failures: number;
  retrieved_at: string;
  type: 'ICEBERG' | 'TRINO' | 'ATHENA';
  timestamp_from: string;
  duration: string;
  stream_id: string;
  stream_title: string;
  id: string;
  partially_retrieved: boolean;
  source_name: string;
  status: 'COMPLETE' | 'PARTIAL';
}
export type PageListResponse= {
  total: number;
  pagination: PaginationInfo;
  defaults: EntityDefaults;
  query: string;
  elements: DataLakeRetrievalResult[];
  attributes: EntityAttribute[];
  sort: string;
  order: 'asc' | 'desc';
}

export const fetchDataLakeRetrievals = async (searchParams: SearchParams): Promise<PaginatedResponse<DataLakeRetrievalResult>> => {
  const url = PaginationURL(
    `/plugins/org.graylog.plugins.datalake/data_lake/retrievals`,
    searchParams.page,
    searchParams.pageSize,
    searchParams.query,
    {
      sort: searchParams.sort.attributeId,
      order: searchParams.sort.direction,
      filters: FiltersForQueryParams(searchParams.filters),
    },
  );

  return fetch('GET', qualifyUrl(url)).then(
    ({ elements, ...rest }) => ({
      ...rest,
      list: elements,
    })
  );
}

export const DataLakeRetrievalsQueryKey = 'data-lake-retrievals';

export const keyFn = (searchParams: SearchParams) => [DataLakeRetrievalsQueryKey, searchParams];

const useDataLakeRetrievals = (searchParams: SearchParams, refetchInterval: number | false = 5000) => {
  const { data, refetch, isLoading, error } = useQuery({
    queryKey: keyFn(searchParams),
    queryFn: () =>
      defaultOnError(
        fetchDataLakeRetrievals(searchParams),
        'Loading Data Lake Retrievals failed with status.',
        'Could not load Data Lake Retrievals.',
      ),
    notifyOnChangeProps: ['data', 'error'],
    refetchInterval,
  });

  return {
    data,
    refetch,
    isLoading,
    error,
  };
};

export default useDataLakeRetrievals;
